import {
  Action,
  Description,
  Grid,
  Illustration,
  BannerList,
  StoriesBannerSection,
  Subtitle,
  Title,
} from '@/scenes/Stories/Banner/styles';
import { SplitText } from '@/utils/gsap/SplitText';

import SVGIllustration from '@/assets/stories-banner.svg';
import List from '@/components/List';
import Button from '@/components/UI/Button';
import { inject, observer } from 'mobx-react';
import { IUIStore } from '@/stores/UI';
import { FC, useEffect, useRef, useState } from 'react';
import { gsap, Power4 } from 'gsap';
import { Remarkable } from 'remarkable';
const md = new Remarkable();

interface StoriesBannerProps {
  uiStore: IUIStore;
  title: string;
  description: string;
  subtitle: string;
  list: {
    item: string;
  }[];
}

const StoriesBanner: FC<any> = ({
  uiStore: { openModal, preloaded },
  title,
  list,
  subtitle,
  description,
}: StoriesBannerProps) => {
  const createStory = () => {
    openModal({ type: 'createStory', instance: '' });
  };

  const bannerList = list.map((i) => i.item);

  const titleRef = useRef(null);
  const illustrationRef = useRef(null);
  const subtitleRef = useRef(null);
  const descriptionRef = useRef(null);
  const listRef = useRef(null);
  const buttonRef = useRef(null);

  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || !preloaded) return;

    const tl = gsap.timeline();

    const titleChildSplit = new SplitText(titleRef.current, {
      type: 'lines',
      linesClass: 'animatedLineChild',
    });
    const titleParentSplit = new SplitText(titleRef.current, {
      type: 'lines',
      linesClass: 'animatedLineParent',
    });

    const subtitleChildSplit = new SplitText(subtitleRef.current, {
      type: 'lines',
      linesClass: 'animatedLineChild',
    });
    new SplitText(subtitleRef.current, {
      type: 'lines',
      linesClass: 'animatedLineParent',
    });

    const descriptionChildSplit = new SplitText(descriptionRef.current, {
      type: 'lines',
      linesClass: 'animatedLineChild',
    });
    new SplitText(descriptionRef.current, {
      type: 'lines',
      linesClass: 'animatedLineParent',
    });

    const listChildSplit = new SplitText(listRef.current, {
      type: 'lines',
      linesClass: 'animatedLineChild',
    });
    const listParentSplit = new SplitText(listRef.current, {
      type: 'lines',
      linesClass: 'animatedLineParent',
    });

    gsap.set(
      [
        titleRef.current,
        subtitleRef.current,
        descriptionRef.current,
        listRef.current,
      ],
      {
        opacity: 1,
      },
    );

    tl.fromTo(
      titleChildSplit.lines,
      {
        transition: 'none',
        transformOrigin: '0% 0%',
        scaleY: 1,
        yPercent: 100,
      },
      {
        duration: 0.75,
        transformOrigin: '0% 0%',
        scaleY: 1,
        opacity: 1,
        y: 0,
        yPercent: 0,
        stagger: 0.075,
        ease: Power4.easeInOut,
        onComplete: () => {
          // @ts-ignore
          titleChildSplit.revert();
        },
      },
    )
      .to(titleParentSplit.lines, { overflow: 'visible' }, 0.2)
      .fromTo(
        subtitleChildSplit.lines,
        {
          transition: 'none',
          transformOrigin: '0% 0%',
          scaleY: 1,
          yPercent: 100,
        },
        {
          duration: 0.75,
          transformOrigin: '0% 0%',
          scaleY: 1,
          opacity: 1,
          y: 0,
          yPercent: 0,
          stagger: 0.075,
          ease: Power4.easeInOut,
          onComplete: () => {
            // @ts-ignore
            // subtitleChildSplit.revert();
          },
        },
        0.3,
      )
      .to(
        illustrationRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: Power4.easeInOut,
        },
        '-=0.5',
      )
      .fromTo(
        descriptionChildSplit.lines,
        {
          transition: 'none',
          transformOrigin: '0% 0%',
          scaleY: 1,
          yPercent: 100,
        },
        {
          duration: 0.75,
          transformOrigin: '0% 0%',
          scaleY: 1,
          opacity: 1,
          y: 0,
          yPercent: 0,
          stagger: 0.075,
          ease: Power4.easeInOut,
          onComplete: () => {
            // @ts-ignore
            descriptionChildSplit.revert();
          },
        },
        '-=0.5',
      )
      .fromTo(
        listChildSplit.lines,
        {
          transition: 'none',
          transformOrigin: '0% 0%',
          scaleY: 1,
          yPercent: 100,
        },
        {
          duration: 0.75,
          transformOrigin: '0% 0%',
          scaleY: 1,
          opacity: 1,
          y: 0,
          yPercent: 0,
          stagger: 0.075,
          ease: Power4.easeInOut,
          onComplete: () => {
            // @ts-ignore
            listChildSplit.revert();
          },
        },
        '-=0.3',
      )
      .set(listParentSplit.lines, { overflow: 'visible' }, 0.2)
      .to(
        buttonRef.current,
        {
          opacity: 1,
          duration: 0.5,
          ease: Power4.easeInOut,
        },
        '-=0.5',
      );

    return () => {
      tl.kill();
    };
  }, [loaded, preloaded]);

  return (
    <StoriesBannerSection>
      <div className="container">
        <Grid>
          <Title ref={titleRef}>{title}</Title>
          <Illustration ref={illustrationRef}>
            <SVGIllustration viewBox="0 0 213 214" />
          </Illustration>
          <Subtitle ref={subtitleRef} className="text-super-small">
            {subtitle}
          </Subtitle>
          <Description
            ref={descriptionRef}
            dangerouslySetInnerHTML={{ __html: md.render(description) }}
          />
          <BannerList>
            <List wide down items={bannerList} ref={listRef} />
          </BannerList>
          <Action>
            {/*@ts-ignore*/}
            <Button ref={buttonRef} type="primary" onClick={createStory}>
              Create story
            </Button>
          </Action>
        </Grid>
      </div>
    </StoriesBannerSection>
  );
};

export default inject('uiStore')(observer(StoriesBanner));
